import { Button } from "@/src/components/Button";
import { CheckBox } from "@/src/components/Fields/CheckBox";
import { PhoneField } from "@/src/components/Fields/PhoneField";
import { SelectField } from "@/src/components/Fields/SelectField";
import { TextField } from "@/src/components/Fields/TextField";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { sendContactUsForm } from "@/src/api/services/contactUs";
import "./index.css";
import { useParams, useSearchParams } from "next/navigation";
import { getProcessInstance } from "@/src/api/services/processInstance";
import { PROCESS_INSTANCE_QUERY } from "@/src/shared/const";
import { ButtonLoader } from "@/src/components/Loaders";
import { FileInput } from "./FileInput";

// TODO: Task write us
interface ContactDataStageProps {
  onSend: () => void;
  onClose: () => void;
}

const clientThemeOptions = [
  {
    label: `Страховий поліс “Автоцивілка”`,
    value: "Страховий поліс “Автоцивілка”",
  },
  {
    label: `Страховий поліс “Зелена картка”`,
    value: "Страховий поліс “Зелена картка”",
  },
  {
    label: `Страховий поліс “Туризм”`,
    value: "Страховий поліс “Туризм”",
  },
  {
    label: `Страховий поліс “Автозахист”`,
    value: "Страховий поліс “Автозахист”",
  },
  {
    label: "Технічні труднощі",
    value: "Технічні труднощі", //
  },
  {
    label: `Інше`,
    value: "Інше",
  },
];
const defaultOptionsMap = {
  oscpv: clientThemeOptions[0],
  "green-card": clientThemeOptions[1],
  tourism: clientThemeOptions[2],
  autoprotect: clientThemeOptions[3],
};

export const ContactDataStage: FC<ContactDataStageProps> = ({
  onSend,
  onClose,
}) => {
  const [orderNumber, setOrderNumber] = useState<string | null>(null);

  const { productSlug } = useParams<{
    productSlug: keyof typeof defaultOptionsMap;
  }>();
  const searchParams = useSearchParams();
  const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);

  const { data: processInstance, isLoading } = useQuery({
    queryKey: ["processInstance", processInstanceId],
    queryFn: () =>
      getProcessInstance({ processInstanceId: processInstanceId ?? "" }),
    enabled: !!processInstanceId && !!productSlug,
  });

  const { control, handleSubmit, resetField, setValue, getValues } =
    useFormContext();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: sendContactUsForm,
    onSuccess: (data) => {
      setOrderNumber(null);
      resetField("orderNumber");
      resetField("clientTheme");
      resetField("clientText");
      resetField("personalDataAgreement");
      resetField("attachments");

      onSend();
    },
  });

  const onSubmit = async () => {
    const formData: any = getValues();

    await mutateAsync({ ...formData, clientTheme: formData.clientTheme?.value });
  };

  useEffect(() => {
    if (processInstance?.order?.number) {
      setOrderNumber(processInstance.order.number);
      setValue("orderNumber", processInstance.order.number);
      setValue("clientTheme", defaultOptionsMap[productSlug]);
      console.log("ORDER NUMBER SEt");
    } else {
      console.log("ORDER NUMBER RESET");
      setOrderNumber(null);
      setValue("orderNumber", null);
      setValue("clientTheme", null);
    }
  }, [processInstance]);

  return (
    <Stack
      className={
        "darkErrorMessage mx-auto my-0 flex max-w-[524px] flex-col gap-6 rounded-xl bg-white p-6 center-modal max-md:static max-md:h-full max-md:w-full max-md:max-w-none max-md:translate-x-0 max-md:translate-y-0 max-md:animate-up-modal max-md:overflow-y-auto max-md:rounded-none max-md:pt-24 md:max-h-[95%]"
      }
    >
      <Stack className={"flex w-full flex-row items-start gap-3"}>
        <Typography
          variant={"h1"}
          component={"h3"}
          className={
            "flex-1 justify-center text-center text-2xl font-semibold max-md:justify-start max-md:text-start"
          }
        >
          {"Зв’яжіться з нами, щоб отримати консультацію"}
        </Typography>
        <IconButton onClick={onClose} className={"max-md:hidden"}>
          <WebShopIcon name={"close"} className="fill-blue" />
        </IconButton>
      </Stack>
      <form
        className={
          "flex flex-col gap-3 overflow-auto rounded-xl bg-dark-blue p-5 max-md:rounded-none max-md:bg-white max-md:p-0"
        }
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextField
          control={control}
          name={"orderNumber"}
          label={"Номер замовлення"}
          placeholder={"Вкажіть номер вашого замовлення"}
          required={!!orderNumber}
          disabled={!!productSlug && !!processInstanceId}
          InputProps={{
            endAdornment: (
              <div className={"px-2"}>
                {isLoading ? <ButtonLoader size={"large"} /> : undefined}
              </div>
            ),
          }}
        />
        <SelectField
          control={control}
          name={"clientTheme"}
          label={"Тема звернення"}
          options={clientThemeOptions}
          placeholder={"З чим у вас виникли проблеми?"}
          required={!!productSlug}
          defaultValue={defaultOptionsMap[productSlug]}
        />
        <TextField
          control={control}
          name={"clientName"}
          label={"Ім’я"}
          placeholder={"Як до вас можна звертатись?"}
          autoComplete={"name"}
        />
        <PhoneField
          label={"Номер телефону"}
          control={control}
          name={"clientPhone"}
          autoComplete={"phone"}
          required={true}
          type={"PhoneInput"}
        />
        <TextField
          label={"Email"}
          control={control}
          name={"clientEmail"}
          placeholder={"exapmle@gmail.com"}
          autoComplete={"email"}
          pattern={new RegExp("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$")}
          patternErrorMessage={"Некоректний email"}
        />
        <TextField
          label={"Повідомлення"}
          control={control}
          name={"clientText"}
          rows={1}
          multiline
          placeholder={"Напишіть, будь ласка, чим ми можемо вам допомогти."}
          required={true}
          sx={{
            "& .MuiInputBase-root": {
              height: "auto",
            },
          }}
        />
        <FileInput name="attachments" label="Обрати файл" />
        <CheckBox
          control={control}
          name={"personalDataAgreement"}
          label={"Даю згоду на використання моїх особистих персональних даних."}
          className={"mr-0"}
          formControlLabelProps={{
            classes: {
              label: "text-white max-md:text-dark-gray",
            },
          }}
          required={true}
        />
        <Box className={"flex w-full max-md:mt-14"}>
          <Button
            fullWidth={false}
            type="submit"
            className={"max-md:w-full"}
            loading={isPending}
          >
            {"Написати нам"}
          </Button>
        </Box>
      </form>
    </Stack>
  );
};
